import React, { useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PORTAL, ADMIN_PORTAL_ROLES } from '../../portalMode';
import AdminSidebar from './AdminSidebar';
import { Toaster } from '../ui/sonner';
import { Button } from '../ui/button';
import { Menu } from 'lucide-react';

const AdminLayout = () => {
  const { isAuthenticated, user, loading } = useAuth();
  const isAdminPortalUser = ADMIN_PORTAL_ROLES.includes(user?.role);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdminPortalUser) {
    // The admin build has no investor dashboard to fall back to.
    return <Navigate to={PORTAL === 'admin' ? '/' : '/dashboard'} replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      <AdminSidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />

      {/* Main Content Area with offset for sidebar (only at lg+ — the
          sidebar is an off-canvas drawer below that, so no offset needed) */}
      <main className="ml-0 lg:ml-64 min-h-screen transition-all duration-300">
        {/* Admin Header Bar */}
        <header className="bg-white border-b border-gray-200 px-3 md:px-6 py-3 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-gray-500 hover:text-gray-900 flex-shrink-0 -ml-1"
                onClick={() => setMobileNavOpen(true)}
                data-testid="admin-mobile-menu-button"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded flex-shrink-0">
                ADMIN
              </span>
              <span className="text-sm text-gray-500 truncate hidden sm:inline">
                Plane Tree Capital Administration
              </span>
            </div>
            <div className="text-sm text-gray-400 hidden md:block flex-shrink-0">
              {new Date().toLocaleDateString('en-ZA', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>

      <Toaster position="top-right" />
    </div>
  );
};

export default AdminLayout;
